import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable ,  BehaviorSubject ,  ReplaySubject } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { catchError } from 'rxjs/internal/operators/catchError';
import { ApiService } from '../ApiService';
import { JwtService } from './jwt.service';
import { Identity } from '../../interfaces/auth';
import { map ,  distinctUntilChanged } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class IdentityService {
  private currentIdentitySubject = new BehaviorSubject<Identity>({} as Identity);
  public currentIdentity = this.currentIdentitySubject.asObservable().pipe(distinctUntilChanged());

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();

  private baseUrl = '/identities';

  constructor(
    private apiService: ApiService,
    private jwtService: JwtService,
    private router: Router,
  ) {
  }

  populate() {
    if (this.jwtService.getIdentity()) {
      this.apiService.get(this.baseUrl + '/status')
        .subscribe(
          () => this.setAuth(this.jwtService.getIdentity()),
          () => {
            this.purgeAuth();
          }
        );
    } else {
      this.purgeAuth();
    }
  }

  setAuth(identity: Identity) {
    this.jwtService.saveIdentity(identity);
    this.currentIdentitySubject.next(identity);
    this.isAuthenticatedSubject.next(true);
  }

  purgeAuth(withRedirect = false) {
    if (this.jwtService.getIdentity()) {
      this.revoksTokens();
    }
    this.jwtService.destroyIdentity();
    this.currentIdentitySubject.next({} as Identity);
    this.isAuthenticatedSubject.next(false);
    if (withRedirect) {
      this.router.navigate(['/auth/login'], { queryParams: { reason: 'session' } });
    }
  }

  attemptAuth(credentials): Observable<Identity> {
    return this.apiService.post(this.baseUrl + '/login', credentials)
      .pipe(map(
        data => {
          this.setAuth(data);
          return data;
        }
      ));
  }

  authWithToken(token): Observable<boolean> {
    return this.apiService.post(this.baseUrl + '/loginWithToken', {
      token: token,
    }).pipe(
      map(
        data => {
          this.setAuth(data);
          return true;
        }
      ),
      catchError(() => {
        this.purgeAuth();
        return of(false);
      })
    );
  }

  refreshAccessToken(): Observable<Identity> {
    const body = {refreshToken: this.jwtService.getRefreshToken()};
    return this.apiService.post(this.baseUrl + '/refresh-token', body)
      .pipe(map(
        data => {
          this.setAuth(data);
          return data;
        }
      ));
  }

  revoksTokens(): void {
    const body = {refreshToken: this.jwtService.getRefreshToken()};
    this.apiService.post(this.baseUrl + '/logout', body).subscribe();
  }

  setPassword(data): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/set-password`, data);
  }

  resetPassword(data): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/reset-password`, data);
  }

  getCurrentIdentity(): Identity {
    return this.currentIdentitySubject.value;
  }

  enable2FA(): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/enable2FA`, []);
  }

  disable2FA(body): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/disable2FA`, body);
  }

  confirm2FA(body): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/confirm2FA`, body);
  }

  loginWithQrCode(qrKeyCode: string): Observable<Identity> {
    const body = {
      qrKeyCode : qrKeyCode
    };
    return this.apiService.post(`${this.baseUrl}/loginWithQrCode`, body)
      .pipe(map(
        data => {
          this.setAuth(data);
          return data;
        }
      ));
  }
}
